import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

// Dynamic CSS import based on theme
function getTopbarStyles(themeColor) {
  if (themeColor && themeColor.toLowerCase() === 'white') {
    return import('./Topbar2.css');
  }
  return import('./Topbar.css');
}

function getStartOfMonth() {
  const today = new Date();
  const start = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1));
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Kampala',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(start);
}

function getEndOfMonth() {
  const today = new Date();
  const end = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0));
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Kampala',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(end);
}

const CriticalNotification = () => (
  <div className="critical-notification">
    <FontAwesomeIcon icon={faExclamationTriangle} />
    <span>CRITICAL Patient Waiting</span>
  </div>
);

function Topbar({ token }) {
  const [permissions, setPermissions] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [message, setMessage] = useState('');
  const [dataAvailable, setDataAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [currentAppointmentIndex, setCurrentAppointmentIndex] = useState(0);
  const [displayingReminder, setDisplayingReminder] = useState(false);
  const [totalReminders, setTotalReminders] = useState(0);
  const [starsVisible, setStarsVisible] = useState(false);
  const [cssLoaded, setCssLoaded] = useState(false);
  const [themeColor, setThemeColor] = useState('');
  const [securityDataLoaded, setSecurityDataLoaded] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [showRewards, setShowRewards] = useState(true);
  const [showCloseButton, setShowCloseButton] = useState(false);

  // Individual loading states
  const [homeLoading, setHomeLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [pharmacyLoading, setPharmacyLoading] = useState(false);
  const [cashierLoading, setCashierLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [restockLoading, setRestockLoading] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [setCategoriesLoading, setSetCategoriesLoading] = useState(false);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [storeLoading, setStoreLoading] = useState(false);

  const startDate = getStartOfMonth();
  const endDate = getEndOfMonth();

  const navigate = useNavigate();

  // Load CSS based on theme
  useEffect(() => {
    const loadCSS = async () => {
      try {
        if (themeColor) {
          await getTopbarStyles(themeColor);
        } else {
          await import('./Topbar.css');
        }
        setCssLoaded(true);
      } catch (error) {
        console.error('Error loading CSS:', error);
        await import('./Topbar.css');
        setCssLoaded(true);
      }
    };

    if ((themeColor || securityDataLoaded) && !cssLoaded) {
      loadCSS();
    }
  }, [themeColor, cssLoaded, securityDataLoaded]);

  // Fetch security/session data
  const fetchSessionData = async () => {
    try {
      const response = await fetch(urls.security, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        setEmployeeData(data);
        if (data.colour) setThemeColor(data.colour);
        if (data.employee_balance > 0) setShowRewards(true);
      }
    } catch (error) {
      console.error('Error fetching session data:', error);
    } finally {
      setSecurityDataLoaded(true);
    }
  };

  // Fetch permissions and performance
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await fetch(urls.fetchpermissions, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (response.ok) {
          const data = await response.json();
          setPermissions(data.permissions || []);
          fetchPerformanceData(token, data.name);
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
    fetchSessionData();
  }, [token]);

  const fetchPerformanceData = async (token, employeeName) => {
    try {
      setLoading(true);
      const response = await fetch(urls.fetchperformance, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, startDate, endDate, section: 'all' }),
      });

      const data = await response.json();

      if (!data.error) {
        data.sort((a, b) => b.percentage - a.percentage);
        const loggedEmployee = data.find(emp => emp.employee_name === employeeName);
        const employeePosition = data.findIndex(emp => emp.employee_name === employeeName) + 1;

        if (loggedEmployee) {
          let stars = 0;
          if (employeePosition === 1) stars = 5;
          else if (employeePosition === 2) stars = 4;
          else if (employeePosition === 3) stars = 3;
          else if (employeePosition === 4) stars = 2;
          else if (employeePosition === 5) stars = 1;

          setSelectedEmployee(loggedEmployee.employee_name);
          setMessage(stars);
          setDataAvailable(true);
          setTimeout(() => setStarsVisible(true), 100);
        }
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Critical patients check
  useEffect(() => {
    let notificationInterval;

    const checkCriticalPatients = () => {
      if (permissions.includes('access-doctors-room')) {
        fetch(urls.checkcritical, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
          .then(r => r.json())
          .then(data => setIsCritical(data.response === 'yes'))
          .catch(err => console.error('Error:', err));
      }
    };

    checkCriticalPatients();
    notificationInterval = setInterval(checkCriticalPatients, 10000);
    return () => clearInterval(notificationInterval);
  }, [permissions, token]);

  // Appointments / reminders
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const now = new Date();
        const kampalaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Kampala' }));
        const hours = kampalaTime.getHours();
        if (hours >= 21 || hours < 6) return;

        const response = await fetch(urls.appointments, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setAppointments(data);
            if (data[0].reminded === 'no') setDisplayingReminder(true);
            setTotalReminders(data.filter(app => app.reminded === 'no').length);
          }
        }
      } catch (error) {
        console.error('Error fetching appointments:', error);
      }
    };

    fetchAppointments();
  }, [token]);

  const handleReminderClick = async () => {
    const currentAppointment = appointments[currentAppointmentIndex];
    const confirmed = window.confirm(
      `Are you sure you have contacted ${currentAppointment.first_name} ${currentAppointment.last_name}?`
    );

    if (confirmed) {
      try {
        const response = await fetch(urls.deletereminder, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ appointment_id: currentAppointment.appointment_id, token }),
        });

        if (response.ok) {
          setAppointments(prev => prev.filter(app => app.appointment_id !== currentAppointment.appointment_id));
          setDisplayingReminder(false);
          setTotalReminders(prev => prev - 1);
        }
      } catch (error) {
        console.error('Error deleting reminder:', error);
      }
    }
  };

  // Navigation helpers
  const navTo = (path, setLoader) => async () => {
    setLoader(true);
    try {
      navigate(`${path}?token=${token}`);
    } finally {
      setLoader(false);
    }
  };

  const fetchHomeToken = async () => {
    try {
      const response = await fetch(urls.dashboardtoken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();
        return data.token;
      }
    } catch (error) {
      console.error('Error fetching home token:', error);
    }
    return null;
  };

  const handleHomeNavigation = async () => {
    setHomeLoading(true);
    try {
      const newToken = await fetchHomeToken();
      if (newToken) navigate(`/dashboard?token=${newToken}`);
    } finally {
      setHomeLoading(false);
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      const response = await fetch(urls.logout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (response.ok) navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleRewardsMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setShowCloseButton((e.clientY - rect.top) < rect.height * 0.25);
  };

  const handleCloseRewards = (e) => {
    e.stopPropagation();
    setShowRewards(false);
  };

  const handlePerformanceClick = () => navigate(`/employeePerformance?token=${token}`);
  const handleRewardClick = () => navigate(`/employeebalance?token=${token}`);

  if (!cssLoaded || !securityDataLoaded) return null;

  // Button config: label, handler, loading state
  const navButtons = [
    {
      label: 'Home ',
      onClick: handleHomeNavigation,
      loading: homeLoading,
    },
    {
      label: 'Sell items',
      onClick: navTo('/selldrugs', setPharmacyLoading),
      loading: pharmacyLoading,
    },
    {
      label: 'Cashier',
      onClick: navTo('/salespage', setCashierLoading),
      loading: cashierLoading,
    },
    {
      label: 'Credits',
      onClick: navTo('/credits', setBillingLoading),
      loading: billingLoading,
    },
    
    {
      label: 'Stock Tracking',
      onClick: navTo('/stocktracking/', setStockLoading),
      loading: stockLoading,
    },
    {
      label: 'Set Categories',
      onClick: navTo('/set-sales-expenses-categories', setSetCategoriesLoading),
      loading: setCategoriesLoading,
    },
    {
      label: 'Reminders',
      onClick: navTo('/patient-appointments/', setAppointmentsLoading),
      loading: appointmentsLoading,
    },
    {
      label: 'Store',
      onClick: navTo('/store', setStoreLoading),
      loading: storeLoading,
    },
    {
      label: 'Log Out',
      onClick: handleLogout,
      loading: logoutLoading,
      isLogout: true,
    },
  ];

  return (
    <>
      <style>{`
        @keyframes starDropIn {
          0% { opacity: 0; transform: translateY(-30px) scale(0.3) rotate(-20deg); }
          60% { opacity: 1; transform: translateY(4px) scale(1.2) rotate(5deg); }
          80% { transform: translateY(-3px) scale(0.95) rotate(-3deg); }
          100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
        }
        @keyframes starPulseGlow {
          0%, 100% { filter: drop-shadow(0 0 2px gold); }
          50% { filter: drop-shadow(0 0 8px gold) drop-shadow(0 0 14px orange); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .loading-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          margin-right: 6px;
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
          vertical-align: middle;
        }
      `}</style>

      <div className="topbar">
        {isCritical && <CriticalNotification />}

        {navButtons.map(({ label, onClick, loading, isLogout }) => (
          <button
            key={label}
            className={`topbar-button${isLogout ? ' logout' : ''}`}
            onClick={onClick}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner" />
                {isLogout ? 'Logging out...' : 'Loading...'}
              </>
            ) : (
              <>
                {isLogout && (
                  <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: '6px' }} />
                )}
                {label}
              </>
            )}
          </button>
        ))}

        {/* Performance stars */}
        {dataAvailable && (
          <div className="performance-section" onClick={handlePerformanceClick}>
            <h3 style={{ fontSize: '14px' }}>{selectedEmployee}</h3>
            <div className="stars">
              {Array.from({ length: 5 }).map((_, index) => {
                const isFilled = index < message;
                return (
                  <svg
                    key={index}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className={`star-icon ${isFilled ? 'filled' : 'empty'}`}
                    style={{
                      opacity: starsVisible ? 1 : 0,
                      animation: starsVisible
                        ? `starDropIn 0.55s cubic-bezier(0.22,1,0.36,1) both${isFilled ? `, starPulseGlow 2.5s ease-in-out ${index * 0.08 + 0.6}s infinite` : ''}`
                        : 'none',
                      animationDelay: starsVisible ? `${index * 0.1}s` : '0s',
                      display: 'inline-block',
                      willChange: 'transform, opacity, filter',
                    }}
                  >
                    <path
                      d="M12 2.5l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.77 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2.5z"
                      style={{
                        fill: isFilled ? 'gold' : 'none',
                        stroke: isFilled ? 'goldenrod' : '#999',
                        strokeWidth: isFilled ? '0.5' : '1',
                      }}
                    />
                  </svg>
                );
              })}
            </div>
          </div>
        )}

        {/* Appointment reminders */}
        {displayingReminder && appointments.length > 0 && (
          <div className="reminder" key={appointments[currentAppointmentIndex].appointment_id}>
            <FontAwesomeIcon
              icon={faTimes}
              className="close-icon"
              onClick={() => setDisplayingReminder(false)}
            />
            <div className="navigation">
              <button
                className="arrow-button left-arrow"
                onClick={() =>
                  setCurrentAppointmentIndex(prev =>
                    prev > 0 ? prev - 1 : appointments.length - 1
                  )
                }
              >
                ←
              </button>
              <p>
                <strong>Reminder:</strong> Please contact the client,{' '}
                <strong>
                  {appointments[currentAppointmentIndex].first_name}{' '}
                  {appointments[currentAppointmentIndex].last_name}
                </strong>.
                <br />
                <strong>Reason:</strong> {appointments[currentAppointmentIndex].appointment_reason}
                <br />
                <strong>Message:</strong> {appointments[currentAppointmentIndex].appointment_message}
                <br />
                <strong>Patients Contact:</strong> {appointments[currentAppointmentIndex].phone_number}
                <br />
                <strong>Date of Appointment:</strong>{' '}
                {appointments[currentAppointmentIndex].date_of_appointment}
              </p>
              <button
                className="arrow-button right-arrow"
                onClick={() =>
                  setCurrentAppointmentIndex(prev => (prev + 1) % appointments.length)
                }
              >
                →
              </button>
            </div>
            <button onClick={handleReminderClick}>
              Click here if the reminder was already made
            </button>
            {totalReminders > 1 && <p>{totalReminders - 1} more reminders left.</p>}
          </div>
        )}
      </div>

      {/* Cash Rewards — bottom left */}
      {showRewards && employeeData && employeeData.employee_balance > 0 && (
        <div
          onClick={handleRewardClick}
          onMouseMove={handleRewardsMouseMove}
          onMouseLeave={() => setShowCloseButton(false)}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            backgroundColor: '#000000',
            color: '#FFFFFF',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
            minWidth: '200px',
            border: '1px solid #333',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Arial, sans-serif',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)';
            e.currentTarget.style.backgroundColor = '#222222';
          }}
        >
          {showCloseButton && (
            <div
              onClick={handleCloseRewards}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 1001,
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ color: '#000', fontSize: '12px' }} />
            </div>
          )}
          <div style={{ fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px', color: '#FFD700' }}>
            🎉 Cash Rewards
          </div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF' }}>
            UGX {employeeData.employee_balance.toLocaleString()}
          </div>
          <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '5px', textAlign: 'center', color: '#CCCCCC' }}>
            Click to view details
          </div>
        </div>
      )}
    </>
  );
}

export default Topbar;